import React from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import blogData from "../data/blogData"; // adjust path to wherever blogData.js lives

const SITE_URL = "https://svkmaidservice.com";

const SEO = () => {
  const location = useLocation();

  const seoData = {
    "/": {
      title: "Maid Service in Mumbai | Verified Full-Time & Part-Time Maids",
      description:
        "Hire trusted maids, babysitters, cooks, drivers and patient care services in Mumbai with SVK Maid Service.",
    },

    "/about-us": {
      title: "Maid Service Agency in Mumbai | SVK Maid Service",
      description:
        "SVK Maid Service provides professional maid, babysitter, patient care and home cleaning services across Mumbai.",
    },

    "/contact": {
      title: "Contact SVK Maid Service Mumbai",
      description:
        "Contact SVK Maid Service for trusted maid, babysitter, cook and cleaning services in Mumbai.",
    },

    "/services": {
      title: "Maid Services in Mumbai | SVK Maid Service",
      description:
        "Explore maid services including babysitter, cooking, driver, patient care and home cleaning in Mumbai.",
    },

    "/services/patient-care-service-in-mumbai": {
      title:
        "Patient Care Services in Mumbai | 24/7 Experienced Nursing Care",
      description:
        "Compassionate & trained patient care attendants for elderly & post-surgery care at home. Verified caregivers for complete peace of mind in Mumbai. Call now!",
    },

    "/services/baby-care-service-in-mumbai": {
      title:
        "Baby Care & Nanny Services in Mumbai | Verified Babysitters",
      description:
        "Ensure your baby’s safety with certified & caring baby care takers. Full-time & part-time nanny services tailored to your needs. Book a verified sitter!",
    },

    "/services/house-maid-service-in-mumbai": {
      title:
        "House Maid Services in Mumbai | Full-Time & Part-Time Maids",
      description:
        "Keep your home clean & organized! Hire background-verified house maids for dusting, cleaning, & daily chores. Book experienced domestic help now.",
    },

    "/services/cooking-service-in-mumbai": {
      title:
        "Home Cook Services in Mumbai | Healthy & Hygienic Meals",
      description:
        "Enjoy delicious home-cooked meals every day! Hire experienced cooks for daily meal preparation. Contact us today!",
    },

    "/services/driver-service-in-mumbai": {
      title:
        "Driver Services in Mumbai | Safe Driving Services",
      description:
        "Need a driver for daily commute or outstation trips? Hire trained, licensed & background-verified personal drivers. Book your driver today!",
    },

    "/services/home-cleaning-service-in-mumbai": {
      title:
        "Home Cleaning Services in Mumbai | Professional House Cleaning Experts",
      description:
        "Give your home a fresh look! Deep cleaning services for kitchens, bathrooms, & entire homes using safe equipment. Get a free quote now!",
    },

    "/services/japa-maid-service-in-mumbai": {
      title:
        "Japa Maid Services in Mumbai | Expert Newborn & Mother Care",
      description:
        "Specialized Japa maids for post-natal mother care & traditional newborn baby massage. Experienced & trained caregivers. Book your Japa maid today!",
    },

    "/faq": {
      title: "FAQs | SVK Maid Service Mumbai",
      description:
        "Frequently asked questions about maid, babysitter, cook and patient care services in Mumbai.",
    },

    "/blog": {
      title: "SVK Maid Blog | Maid Service Tips Mumbai",
      description:
        "Read helpful maid service tips, guides and home care blogs from SVK Maid Service Mumbai.",
    },

    "/privacy-policy": {
      title: "Privacy Policy | SVK Maid Service",
      description:
        "Read the privacy policy of SVK Maid Service Mumbai.",
    },

    "/terms-conditions": {
      title: "Terms & Conditions | SVK Maid Service",
      description:
        "Read terms and conditions of SVK Maid Service Mumbai.",
    },
  };

  /*
   * ============================================
   * CLEAN CURRENT PATH
   * ============================================
   *
   * Homepage:
   * /
   *
   * Other pages:
   * /about-us
   * /services
   *
   * /about-us/ → /about-us
   * /services/ → /services
   */

  const cleanPath =
    location.pathname === "/"
      ? "/"
      : location.pathname.replace(/\/+$/, "");

  /*
   * ============================================
   * CANONICAL URL
   * ============================================
   */

  const currentUrl = `${SITE_URL}${cleanPath}`;

  /*
   * ============================================
   * BLOG DETAIL PAGES (slug based, per-post SEO)
   * ============================================
   *
   * Example:
   * /blog/how-to-hire-a-maid-in-mumbai
   */

  if (cleanPath.startsWith("/blog/")) {
    const slug = cleanPath.replace("/blog/", "");
    const post = blogData.find((b) => b.slug === slug);

    const blogTitle = post
      ? `${post.title} | SVK Maid Service`
      : "SVK Maid Blog | Maid Service Tips Mumbai";

    const blogDescription = post
      ? post.excerpt
      : "Read helpful maid service tips and guides from SVK Maid Service Mumbai.";

    const blogImage = post?.image
      ? post.image // resolved asset URL from the bundler
      : `${SITE_URL}/svk-maid-logo.png`;

    return (
      <Helmet>

        {/* TITLE */}
        <title>{blogTitle}</title>

        {/* DESCRIPTION */}
        <meta
          name="description"
          content={blogDescription}
        />

        {/* CANONICAL */}
        <link
          rel="canonical"
          href={currentUrl}
        />

        {/* OPEN GRAPH */}

        <meta
          property="og:title"
          content={blogTitle}
        />

        <meta
          property="og:description"
          content={blogDescription}
        />

        <meta
          property="og:type"
          content="article"
        />

        <meta
          property="og:url"
          content={currentUrl}
        />

        <meta
          property="og:image"
          content={blogImage}
        />

        {post?.date && (
          <meta
            property="article:published_time"
            content={post.date}
          />
        )}

        {/* TWITTER */}

        <meta
          name="twitter:card"
          content="summary_large_image"
        />

        <meta
          name="twitter:title"
          content={blogTitle}
        />

        <meta
          name="twitter:description"
          content={blogDescription}
        />

        <meta
          name="twitter:image"
          content={blogImage}
        />

      </Helmet>
    );
  }

  /*
   * ============================================
   * GET SEO DATA
   * ============================================
   */

  const currentSEO =
    seoData[cleanPath] || {
      title: "SVK Maid Service",
      description:
        "Professional maid services in Mumbai.",
    };

  return (
    <Helmet>

      {/* ========================================
          TITLE
      ======================================== */}

      <title>{currentSEO.title}</title>

      {/* ========================================
          META DESCRIPTION
      ======================================== */}

      <meta
        name="description"
        content={currentSEO.description}
      />

      {/* ========================================
          CANONICAL
      ======================================== */}

      <link
        rel="canonical"
        href={currentUrl}
      />

      {/* ========================================
          OPEN GRAPH
      ======================================== */}

      <meta
        property="og:title"
        content={currentSEO.title}
      />

      <meta
        property="og:description"
        content={currentSEO.description}
      />

      <meta
        property="og:type"
        content="website"
      />

      <meta
        property="og:url"
        content={currentUrl}
      />

      <meta
        property="og:image"
        content={`${SITE_URL}/svk-maid-logo.png`}
      />

      {/* ========================================
          TWITTER
      ======================================== */}

      <meta
        name="twitter:card"
        content="summary_large_image"
      />

      <meta
        name="twitter:title"
        content={currentSEO.title}
      />

      <meta
        name="twitter:description"
        content={currentSEO.description}
      />

      <meta
        name="twitter:image"
        content={`${SITE_URL}/svk-maid-logo.png`}
      />

    </Helmet>
  );
};

export default SEO;